import { useState, useContext } from "react";
import { LanguageContext } from "../../../constant/LanguageContext";
import { apiFetch } from "../../../context/AuthContext";
import { translations } from "../constants/translations";
import { getTrimmedCompanyContext, encodePathSegment } from "../utils/encodeUtils";
import { safeUserLabel } from "../utils/adminLevelUtils";

const API_BASE_URL = `${process.env.REACT_APP_BASE_URL}/api`;

// DEPARTMENT rule grouping value: the department NAME first, with legacy
// fallbacks for user rows coming from endpoints that still emit employeeType.
const getUserDepartment = (u) =>
  u?.employee?.department?.name ??
  u?.employee?.position ??
  u?.employee?.employeeType;

export const useVisibilityRules = (selectedCompany, onSuccess, onError) => {
  const { language } = useContext(LanguageContext);
  const t = translations[language];

  const [selectedLevel,          setSelectedLevel]          = useState(null);
  const [loading,                setLoading]                = useState(false);
  const [selectedCompanies,      setSelectedCompanies]      = useState([]);
  const [departmentsByCompany,   setDepartmentsByCompany]   = useState({});
  const [selectedDepartments,    setSelectedDepartments]    = useState({});
  const [usersByCompany,         setUsersByCompany]         = useState({});
  const [selectedUsers,          setSelectedUsers]          = useState({});
  const [search,                 setSearch]                 = useState("");

  const fetchDepartments = async (clientName, companyName) => {
    const { client, company } = getTrimmedCompanyContext(clientName, companyName);
    if (!client || !company) return;
    try {
      const res  = await apiFetch(
        `${API_BASE_URL}/admin-levels/visibility/options/departments/${encodePathSegment(client)}/${encodePathSegment(company)}`,
        { method: "GET" }
      );
      const data = await res.json();
      const list = data?.departments || [];
      setDepartmentsByCompany((prev) => ({ ...prev, [company]: list }));
      return list;
    } catch (err) {
      setDepartmentsByCompany((prev) => ({ ...prev, [companyName]: [] }));
      onError?.(err.message);
      return [];
    }
  };

  const fetchUsers = async (clientName, companyName, employeeTypes = []) => {
    const { client, company } = getTrimmedCompanyContext(clientName, companyName);
    if (!client || !company) return;
    try {
      const query = new URLSearchParams({
        clientName: client, workingCompany: company,
        ...(employeeTypes.length ? { employeeTypes: employeeTypes.join(",") } : {}),
      }).toString();
      const res  = await apiFetch(`${API_BASE_URL}/admin-levels/visibility/options/users?${query}`, { method: "GET" });
      const data = await res.json();
      const list = data?.users || [];
      setUsersByCompany((prev) => ({ ...prev, [company]: list }));

      setSelectedUsers((prevUsers) => {
        const current = prevUsers[company] || [];
        const fromDepts = list
          .filter((u) => employeeTypes.includes(getUserDepartment(u) || ""))
          .map((u) => u.id);
        const manual = current.filter((id) => {
          const u = list.find((x) => x.id === id);
          return !u || !employeeTypes.includes(getUserDepartment(u) || "");
        });
        return { ...prevUsers, [company]: [...new Set([...fromDepts, ...manual])] };
      });
      return list;
    } catch (err) {
      setUsersByCompany((prev) => ({ ...prev, [companyName]: [] }));
      onError?.(err.message);
      return [];
    }
  };

  const open = async (level) => {
    if (!level?.id) return;
    setSelectedLevel(level);
    setSearch("");
    setLoading(true);
    try {
      const res   = await apiFetch(`${API_BASE_URL}/admin-levels/${level.id}/visibility`, { method: "GET" });
      const data  = await res.json();
      const rules = data?.rules || [];

      const companySet = new Set();
      const deptMap    = {};
      const userMap    = {};

      rules.forEach((r) => {
        const co = String(r.targetWorkingCompany || "").trim();
        if (!co) return;
        companySet.add(co);
        if (r.scopeType === "DEPARTMENT" && r.targetEmployeeType)
          deptMap[co] = [...new Set([...(deptMap[co] || []), r.targetEmployeeType])];
        if (r.scopeType === "USER" && r.targetInfinityCode)
          userMap[co] = [...new Set([...(userMap[co] || []), r.targetInfinityCode])];
      });

      const cos = Array.from(companySet);
      setSelectedCompanies(cos);
      setSelectedDepartments(deptMap);

      const userIdsByCompany = {};
      await Promise.all(
        cos.map(async (co) => {
          await fetchDepartments(selectedCompany.client, co);
          const list  = await fetchUsers(selectedCompany.client, co, deptMap[co] || []);
          const codes = userMap[co] || [];
          userIdsByCompany[co] = (list || []).filter((u) => codes.includes(u?.infinityCode)).map((u) => u.id);
        })
      );
      setSelectedUsers(userIdsByCompany);
    } catch (err) {
      onError?.(err.message || "Failed to load visibility rules");
    } finally {
      setLoading(false);
    }
  };

  const toggleCompany = async (companyName) => {
    const co   = String(companyName || "").trim();
    const isOn = selectedCompanies.map((c) => String(c).trim()).includes(co);
    const next = isOn
      ? selectedCompanies.filter((c) => String(c).trim() !== co)
      : [...new Set([...selectedCompanies, co])];
    setSelectedCompanies(next);
    if (isOn) {
      setSelectedDepartments((p) => { const c = { ...p }; delete c[co]; return c; });
      setSelectedUsers((p)       => { const c = { ...p }; delete c[co]; return c; });
    } else {
      await fetchDepartments(selectedCompany.client, co);
    }
  };

  const toggleSelectAllCompanies = async (allCompanies) => {
    const isAll = allCompanies.every((c) => selectedCompanies.includes(c));
    if (isAll) {
      setSelectedCompanies([]);
      setSelectedDepartments({});
      setSelectedUsers({});
    } else {
      setSelectedCompanies(allCompanies);
      await Promise.all(allCompanies.map(async (co) => {
        await fetchDepartments(selectedCompany.client, co);
        await fetchUsers(selectedCompany.client, co, []);
      }));
    }
  };

  const toggleDepartment = async (companyName, dept) => {
    const co      = String(companyName || "").trim();
    const current = selectedDepartments[co] || [];
    const next    = current.includes(dept) ? current.filter((d) => d !== dept) : [...current, dept];
    setSelectedDepartments((p) => ({ ...p, [co]: next }));
    await fetchUsers(selectedCompany.client, co, next);
  };

  const toggleSelectAllDepartments = async (companyName) => {
    const co   = String(companyName || "").trim();
    const list = departmentsByCompany[co] || [];
    const cur  = selectedDepartments[co] || [];
    const isAll = list.length === cur.length && list.every((d) => cur.includes(d));
    const next  = isAll ? [] : list.slice();
    setSelectedDepartments((p) => ({ ...p, [co]: next }));
    await fetchUsers(selectedCompany.client, co, next);
  };

  const toggleSelectAllDepartmentsGlobal = async () => {
    const isAll = selectedCompanies.every((co) => {
      const list = departmentsByCompany[co] || [];
      const cur  = selectedDepartments[co] || [];
      return list.length === cur.length && list.every((d) => cur.includes(d));
    });
    const next = {};
    await Promise.all(selectedCompanies.map(async (co) => {
      const list = departmentsByCompany[co] || [];
      next[co] = isAll ? [] : list.slice();
      await fetchUsers(selectedCompany.client, co, next[co]);
    }));
    setSelectedDepartments(next);
  };

  const toggleUser = (companyName, userId) => {
    const co  = String(companyName || "").trim();
    const cur = selectedUsers[co] || [];
    setSelectedUsers((p) => ({
      ...p,
      [co]: cur.includes(userId) ? cur.filter((x) => x !== userId) : [...cur, userId],
    }));
  };

  const toggleSelectAllDepartmentUsers = (companyName, dept) => {
    const co       = String(companyName || "").trim();
    const list     = usersByCompany[co] || [];
    const deptIds  = list.filter((u) => (getUserDepartment(u) || "-") === dept).map((u) => u.id);
    const cur      = selectedUsers[co] || [];
    const isAll    = deptIds.every((id) => cur.includes(id));
    setSelectedUsers((p) => ({
      ...p,
      [co]: isAll ? cur.filter((id) => !deptIds.includes(id)) : [...new Set([...cur, ...deptIds])],
    }));
  };

  const toggleSelectAllCompanyUsers = (companyName) => {
    const co    = String(companyName || "").trim();
    const list  = usersByCompany[co] || [];
    const cur   = selectedUsers[co] || [];
    const isAll = list.length === cur.length && list.every((u) => cur.includes(u.id));
    const next  = isAll ? [] : list.map((u) => u.id);
    setSelectedUsers((p) => ({ ...p, [co]: next }));
  };

  const toggleSelectAllUsersGlobal = () => {
    const isAll = selectedCompanies.every((co) => {
      const list = usersByCompany[co] || [];
      const cur  = selectedUsers[co] || [];
      return list.length === cur.length && list.every((u) => cur.includes(u.id));
    });
    const next = {};
    selectedCompanies.forEach((co) => {
      const list = usersByCompany[co] || [];
      next[co] = isAll ? [] : list.map((u) => u.id);
    });
    setSelectedUsers(next);
  };

  const save = async (onRefresh) => {
    if (!selectedLevel?.id) return;
    setLoading(true);
    try {
      const rules = [];
      selectedCompanies.forEach((co) => {
        const company        = String(co || "").trim();
        const depts          = selectedDepartments[company] || [];
        const userIds        = selectedUsers[company] || [];
        const allUsers       = usersByCompany[company] || [];
        const allDepts       = departmentsByCompany[company] || [];
        const allDeptsOn     = allDepts.length > 0 && allDepts.every((d) => depts.includes(d));
        const allUsersOn     = allUsers.length > 0 && allUsers.every((u) => userIds.includes(u.id));

        if ((allDeptsOn && allUsersOn) || (depts.length === 0 && userIds.length === 0)) {
          rules.push({ scopeType: "COMPANY", targetWorkingCompany: company });
          return;
        }

        const handled = new Set();
        depts.forEach((dept) => {
          const deptUsers = allUsers.filter((u) => (getUserDepartment(u) || "") === dept);
          const deptIds   = deptUsers.map((u) => u.id);
          const allOn     = deptIds.length > 0 && deptIds.every((id) => userIds.includes(id));
          if (allOn) {
            rules.push({ scopeType: "DEPARTMENT", targetWorkingCompany: company, targetEmployeeType: dept });
            deptIds.forEach((id) => handled.add(id));
          } else {
            deptIds.filter((id) => userIds.includes(id)).forEach((uid) => {
              const u = allUsers.find((x) => x.id === uid);
              if (u?.infinityCode) {
                rules.push({ scopeType: "USER", targetWorkingCompany: company, targetInfinityCode: u.infinityCode, targetEmployeeType: getUserDepartment(u) || null });
                handled.add(uid);
              }
            });
          }
        });

        userIds.forEach((uid) => {
          if (!handled.has(uid)) {
            const u = allUsers.find((x) => x.id === uid);
            if (u?.infinityCode)
              rules.push({ scopeType: "USER", targetWorkingCompany: company, targetInfinityCode: u.infinityCode, targetEmployeeType: getUserDepartment(u) || null });
          }
        });
      });

      await apiFetch(`${API_BASE_URL}/admin-levels/${selectedLevel.id}/visibility`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rules }),
      });
      onSuccess?.(t.visibilitySaved);
      onRefresh?.();
      return true;
    } catch (err) {
      onError?.(err.message || "Failed to save visibility rules");
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    selectedLevel, setSelectedLevel, loading, search, setSearch,
    selectedCompanies, departmentsByCompany, selectedDepartments,
    usersByCompany, selectedUsers,
    open, toggleCompany, toggleSelectAllCompanies,
    toggleDepartment, toggleSelectAllDepartments, toggleSelectAllDepartmentsGlobal,
    toggleUser, toggleSelectAllDepartmentUsers, toggleSelectAllCompanyUsers, toggleSelectAllUsersGlobal,
    save,
  };
};